import React, { useEffect, useState } from 'react';
import { Table, Input, Modal, Form, message, Upload, Button, Breadcrumb } from 'antd';
import { getCategories } from '../../common/services';
import { LoadableContent } from '../../components';
import {
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  UploadOutlined,
  RightOutlined
} from '@ant-design/icons';

import { Link } from 'react-router-dom';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedCategorie, setSelectedCategorie] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = () => {
    setLoading(true);
    getCategories
      .get()
      .then(setCategories)
      .catch((err) => {
        console.error('Erreur lors du chargement des catégories :', err);
        // alert('Impossible de charger les catégories');
      })
      .finally(() => setLoading(false));
  };

  const filteredCategories = categories.filter((categorie) =>
    categorie.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (record) => {
    setSelectedCategorie(record);
    form.setFieldsValue({
      name: record.name,
      image: record.image,
    });
    setEditModalOpen(true);
  };

  const handleDelete = (record) => {
    // console.log("Suppression catégorie :", record); 
    Modal.confirm({

      title: `Supprimer la catégorie "${record.name}" ?`,
      icon: <ExclamationCircleOutlined />,
      content: 'Cette action est irréversible.',
      okText: 'Oui, supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          console.log("Tentative de suppression de la catégorie :", record._id);
          await getCategories.delete(record._id); //attendre la suppression
          message.success('Catégorie supprimée avec succès');
          fetchCategories(); //maintenant, recharge les données à jour
        } catch (error) {
          console.error('Erreur suppression :', error);
          message.error("Erreur lors de la suppression");
        }
      },
    });
  };


  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedCategorie) return;

      const formData = new FormData();
      formData.append("name", values.name);

      // Gérer l’image si elle est modifiée
      const fileList = values.image?.fileList;
      if (fileList && fileList.length > 0) {
        const file = fileList[0].originFileObj;
        formData.append("image", file);
      }

      await getCategories.put(selectedCategorie._id, formData); // 👈

      message.success("Catégorie mise à jour");
      setEditModalOpen(false);
      fetchCategories();
    } catch (error) {
      console.error("Erreur de mise à jour :", error);
      message.error("Erreur lors de la mise à jour");
    }
  };



  const columns = [
    {
      title: 'Libéllé de la catégorie',
      dataIndex: 'name',
      key: 'name',
      filters: [
        ...new Set(categories.map((categorie) => categorie.name)),
      ].map((name) => ({
        text: name,
        value: name,
      })),
      onFilter: (value, record) => record.name === value,
      filterSearch: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      render: (text, record) => (
        <img
          src={record.image.replace(/\\/g, '/')}
          alt={record.name}
          style={{ width: 40, height: 40, objectFit: 'cover' }}
        />
      ),
    },
    {
      title: 'Date de création',
      dataIndex: 'createdAt',
      key: 'createdAt',
      filters: [
        ...new Set(categories.map((categorie) => categorie.createdAt)),
      ].map((createdAt) => ({
        text: createdAt,
        value: createdAt,
      })),
      onFilter: (value, record) => record.createdAt === value,
      filterSearch: true,
      sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <div className="d-flex gap-2">
          <EditOutlined
            onClick={() => handleEdit(record)}
            style={{ color: '#faad14', fontSize: '18px', cursor: 'pointer' }}
          />
          <DeleteOutlined
            onClick={() => handleDelete(record)}
            style={{ color: '#ff4d4f', fontSize: '18px', cursor: 'pointer' }}
          />
        </div>
      ),
    },
  ];

  return (
    <LoadableContent isLoading={isLoading}>
      <div className="content-wrapper">
        <div className="container-xxl flex-grow-1 container-p-y">
          <div className="row">
            <div className="col-md-12">
              <Breadcrumb
                separator={<RightOutlined style={{ fontSize: '16px', color: '#999', fontWeight: 'bolder' }} />}
                style={{ marginBottom: 16 }}
              >
                <Breadcrumb.Item>
                  <Link to="/dashboard" style={{ fontSize: '16px', fontWeight: 'bolder' }}>🏠 ACCUEIL</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  <span style={{ fontSize: '16px', fontWeight: 'bolder' }}>👥 LISTE DE SERVICE</span>
                </Breadcrumb.Item>
              </Breadcrumb>
              <div className="card mb-4">
                <div className="card-body">
                  <div className="d-flex justify-content-end mb-3">
                    <Input
                      placeholder="Rechercher par nom"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ width: 300 }}
                      allowClear
                    />
                  </div>
                  <Table
                    columns={columns}
                    dataSource={filteredCategories.map((categorie) => ({
                      ...categorie,
                      key: categorie._id,
                    }))}
                    pagination={{ pageSize: 5 }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal d’édition */}
        <Modal
          title="Modifier la catégorie"
          open={isEditModalOpen}
          onCancel={() => setEditModalOpen(false)}
          onOk={handleEditSubmit}
          okText="Enregistrer"
          cancelText="Annuler"
        >
          <Form form={form} layout="vertical">
            <Form.Item
              label="Nom"
              name="name"
              rules={[{ required: true, message: 'Le libéllé est requis' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item label="Image" name="image">
              <Upload
                beforeUpload={() => false} // Ne pas uploader immédiatement
                maxCount={1}
                listType="picture"
              >
                <Button icon={<UploadOutlined />}>Changer l’image</Button>
              </Upload>
            </Form.Item>

          </Form>
        </Modal>
      </div>
    </LoadableContent>
  );
};

export default Categories;
